'use client';

import { faChevronRight, faCoin } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Group, Progress, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core';

import { formatCreditCount } from './format';
import { coinBadge, creditUsageCard, creditUsageCardButton, meterColor } from './styles.css';
import { useCreditSummary } from './use-credit-summary';

interface CreditUsageMenuItemProps {
  workspaceId: string;
  onOpenUsage: () => void;
}

interface CreditUsageRowContentProps {
  primaryLabel: string;
  secondaryLabel?: string;
  progressValue?: number;
}

/**
 * Card body: the coin badge, then a text column whose first line pairs the label
 * with the chevron. Every variant carries the chevron. The line heights and gaps
 * are the design's, so each variant lands on its drawn height: 73px with a meter
 * and a second line, 60px with two lines, 53px with a meter alone.
 */
function CreditUsageRowContent({
  primaryLabel,
  secondaryLabel,
  progressValue,
}: CreditUsageRowContentProps) {
  return (
    <Group align="center" wrap="nowrap" gap="xs" w="100%">
      <ThemeIcon size={28} radius={8} variant="light" color="dark" className={coinBadge}>
        <FontAwesomeIcon icon={faCoin} size="xs" />
      </ThemeIcon>
      <Stack gap={8} flex="1 1 auto">
        <Stack gap={0}>
          <Text fw={600} size="sm" lh="16px">
            {primaryLabel}
          </Text>
          {secondaryLabel != null && (
            <Text c="dimmed" size="xs" lh="20px">
              {secondaryLabel}
            </Text>
          )}
        </Stack>
        {progressValue != null && (
          <Progress value={progressValue} size={5} radius={2} color={meterColor} />
        )}
      </Stack>
      {/* Outside the text column so it centers against the card, not the first line. */}
      <FontAwesomeIcon icon={faChevronRight} size="sm" />
    </Group>
  );
}

/**
 * Placeholder carrying the card's geometry while the summary loads, so the menu
 * reserves the space instead of letting the row pop in and shove the items below
 * it down. Two lines and no meter: the bars sit inside the same 16px and 20px
 * line boxes the real text uses, so the card lands on its loaded height.
 */
function CreditUsageRowSkeleton() {
  return (
    <Box className={creditUsageCard} aria-hidden>
      <Group align="center" wrap="nowrap" gap="xs" w="100%">
        <Skeleton height={28} width={28} radius={8} />
        <Stack gap={0} flex="1 1 auto">
          <Skeleton height={10} my={3} width="65%" />
          <Skeleton height={10} my={5} width="45%" />
        </Stack>
      </Group>
    </Box>
  );
}

export function CreditUsageMenuItem({ workspaceId, onOpenUsage }: CreditUsageMenuItemProps) {
  const { loading, summary, planKind, purchasedMilli, consumedFromPacksMilli } =
    useCreditSummary(workspaceId);

  if (loading) {
    return <CreditUsageRowSkeleton />;
  }

  // The summary failed to load: stay out of the menu rather than showing a row
  // with nothing behind it.
  if (!summary) {
    return null;
  }

  const consumedThisMonth = formatCreditCount(summary.consumedThisMonthMilli);

  const content = (() => {
    if (planKind === 'CREDIT_PACKS') {
      const percentageConsumed =
        purchasedMilli > 0 ? Math.min(100, (consumedFromPacksMilli / purchasedMilli) * 100) : 0;

      return (
        <CreditUsageRowContent
          primaryLabel="Credits used this period"
          secondaryLabel={`${formatCreditCount(consumedFromPacksMilli)} / ${formatCreditCount(purchasedMilli)}`}
          progressValue={percentageConsumed}
        />
      );
    }

    if (planKind === 'UNLIMITED') {
      return (
        <CreditUsageRowContent primaryLabel={`${consumedThisMonth} credits used this period`} />
      );
    }

    return (
      <CreditUsageRowContent
        primaryLabel={`${consumedThisMonth} credits used this period`}
        secondaryLabel="View usage breakdown"
      />
    );
  })();

  return (
    <Box component="button" type="button" onClick={onOpenUsage} className={creditUsageCardButton}>
      {content}
    </Box>
  );
}
